import React from 'react';
import { Helmet } from 'react-helmet-async';
import { DEFAULT_SEO, BASE_URL } from '../utils/seoConstants';
import { generateOrganizationSchema } from '../utils/schemaGenerators';

/**
 * SEOHead - Reusable component for meta tags and structured data
 *
 * @param {string} title - Page title (with fallback)
 * @param {string} description - Meta description (with fallback)
 * @param {string} canonicalUrl - Canonical URL for the page
 * @param {string} ogImage - Open Graph image URL
 * @param {Object|Array} schema - JSON-LD structured data object(s)
 * @param {boolean} noindex - Whether to noindex the page
 * @param {string} ogType - Open Graph type (default: 'website')
 * @param {Object} article - Article metadata for og:article tags
 */
const SEOHead = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  schema,
  noindex = false,
  ogType = 'website',
  article
}) => {
  const pageTitle = title || DEFAULT_SEO.title;
  const pageDescription = description || DEFAULT_SEO.description;
  const pageImage = ogImage || DEFAULT_SEO.image;
  const canonical = canonicalUrl ?
    (canonicalUrl.startsWith('http') ? canonicalUrl : `${BASE_URL}${canonicalUrl}`) :
    undefined;

  // Always include Organization schema
  const orgSchema = generateOrganizationSchema();

  // Combine schemas into array
  const allSchemas = schema ?
    (Array.isArray(schema) ? [orgSchema, ...schema] : [orgSchema, schema]) :
    [orgSchema];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />

      {/* Article specific OG tags */}
      {ogType === 'article' && article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          <meta property="article:author" content="1crew3" />
          {article.section && (
            <meta property="article:section" content={article.section} />
          )}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      {DEFAULT_SEO.twitterHandle && (
        <meta name="twitter:site" content={DEFAULT_SEO.twitterHandle} />
      )}

      {/* JSON-LD Structured Data */}
      {allSchemas.map((schemaItem, index) => (
        <script
          key={`schema-${index}`}
          type="application/ld+json"
        >
          {JSON.stringify(schemaItem)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
